import { Component, OnInit } from '@angular/core';
import { EmployeeService } from '../employee.service';
import { DataTableHeaderOption } from '@dxo/ng-swift-ui';
import {
  CoverageStatus,
  CoverageTableData,
  DependentData,
  EmployeeData,
  CoverageBucketCodes
} from '../employee-page.types';
import {
  ApiResponseOfMemberDetailSearchResponse,
  MemberClient,
  MemberCoverage,
  MemberDependent,
  MemberDependentGender,
  MemberDependentRelationship,
  MemberDependentStatus,
  MemberDetailResponse,
  MemberDetailSearchRequest,
  MemberSmokerStatus
} from '@benefitsui/app/api/benefits-clients';
import { Observable } from 'rxjs';
import { CurrencyPipe } from '@angular/common';
import { Constants } from '../../billing-page/billing-page.types';
import { forEach } from 'lodash';

@Component({
  selector: 'app-employee-detail',
  templateUrl: './detail.component.html',
  styleUrls: ['./detail.component.scss']
})
export class EmployeeDetailComponent implements OnInit {
  Benefit = 'Benefit';
  BillDivision = 'Bill division';
  Birthdate = 'Birth date';
  Class = 'Class';
  Description = 'Description';
  Disabled = 'Disabled';
  Effective = 'Effective date';
  EmployeeCardHeader = 'Employee information';
  Gender = 'Gender';
  HireDate = 'Hire date';
  InsuranceCardHeader = 'Insurance coverage';
  ModalTitle = 'Covered dependents';
  Name = 'Name';
  Occupation = 'Occupation';
  Premium = 'Premium';
  Relationship = 'Relationship';
  Salary = 'Salary';
  SmokerStatus = 'Smoker status';
  SSN = 'Social Security';
  Status = 'Status';
  Student = 'Full-time student';
  Termination = 'Termination date';
  TotalAmountLabel = 'Total premium';
  isLoading = true;
  totalAmount = 0;

  lifeCoverageData: CoverageTableData[] = [];

  disabilityElectionsData: CoverageTableData[] = [];

  absenceAndPaidLeaveData: CoverageTableData[] = [];

  constructor(
    private employeeService: EmployeeService,
    private memberClient: MemberClient
  ) {
    this.contractId = this.employeeService.contractId;
    this.employeeId = this.employeeService.employeeId;
    this.detailRequestParams = {
      individualId: this.employeeId,
      contractId: this.contractId
    };
  }

  // :  Data members
  contractId: string;
  detailRequestParams: MemberDetailSearchRequest;
  employeeId: string;

  statusColor = '#8ABF2E';
  showModal = false;
  modalList: string[] = [];

  data$!: Observable<MemberDetailResponse>;
  employeeInfo: EmployeeData = {
    fullName: Constants.StringEmpty,
    firstName: Constants.StringEmpty,
    lastName: Constants.StringEmpty,
    birthdate: Constants.StringEmpty,
    gender: Constants.StringEmpty,
    status: Constants.StringEmpty,
    statusColor: Constants.StringEmpty,
    occupation: Constants.StringEmpty,
    ssn: Constants.StringEmpty,
    salary: Constants.StringEmpty,
    hireDate: Constants.StringEmpty,
    smokerStatus: Constants.StringEmpty,
    billDivision: Constants.StringEmpty,
    billDivisionNumber: Constants.StringEmpty
  };

  dependentsList?: DependentData[] = [];

  ngOnInit(): void {
    this.memberClient
      .detail(this.detailRequestParams)
      .subscribe((response) => {
        this.transformData(response);
        if (
          response.data?.memberDetail?.dependents !== null &&
          response.data?.memberDetail?.dependents
        ) {
          const dependents = response.data.memberDetail.dependents;
          const coverages = response.data.memberDetail.coverage;
          let coverageStatus = CoverageStatus.Unknown;
          dependents.forEach((dependent, index) => {
            coverageStatus = this.updateCoverageStatus(coverages, dependent);
            this.dependentsList![index].coverageStatus = coverageStatus;
            this.dependentsList![index].statusColor =
              this.getStatusColor(coverageStatus);
          });
        }
      })
      .add(() => {
        this.isLoading = false;
      });
  }

  transformData(response: ApiResponseOfMemberDetailSearchResponse) {
    const memberDetail = response.data?.memberDetail;
    this.employeeInfo = this.formatEmployee(memberDetail);
    const dependents = memberDetail?.dependents;
    const coverages = memberDetail?.coverage;
    coverages?.forEach((coverage) => {
      this.formatCoverages(coverage, dependents);
    });
    dependents?.forEach((item) => {
      this.formatDependent(item);
    });

    return memberDetail;
  }

  formatCoverages(
    coverage: MemberCoverage,
    dependents: MemberDependent[] | undefined
  ) {
    if (coverage && coverage.items) {
      forEach(coverage.items, (item) => {
        // check if coverage is current and has is a life coverage
        if (
          coverage.current === true &&
          item.bucketCode === CoverageBucketCodes.LifeAdd
        ) {
          this.lifeCoverageData.push({
            description: item.coverageLabel ?? Constants.StringEmpty,
            class: coverage.classCode ?? Constants.StringEmpty,
            benefit:
              this.formatCurrency(item.benefitAmount) ?? Constants.StringEmpty,
            premium:
              this.formatCurrency(item.premiumTotal) ?? Constants.StringEmpty,
            effectiveDate:
              this.formatDate(coverage.effectiveDate) ?? Constants.StringEmpty,
            terminationDate:
              this.formatDate(coverage.terminationDate) ??
              Constants.StringEmpty,
            dependents: this.checkIfDependent(coverage.sortNames, dependents)
          });
          this.totalAmount += item.premiumTotal ?? 0;
        }
      });
    }
  }

  // return a list of strings if dependents
  checkIfDependent(
    sortNames: string[] | undefined,
    dependents: MemberDependent[] | undefined
  ) {
    const dependentNames: string[] = [];
    if (sortNames && dependents) {
      sortNames.forEach((name) => {
        const dependent = dependents.find((dep) => dep.fullName === name);
        if (dependent && dependent.fullName) {
          dependentNames.push(dependent.fullName);
        }
      });
    }
    return dependentNames;
  }

  /* Scenarios -
    "Active" - At least one CURRENT coverage includes the given dependent and does not have a stop date
    "Inactive" - No CURRENT coverages include the dependent
    "Planned termination" - All CURRENT coverages that include the dependent have a stop date */
  updateCoverageStatus(
    coverages: MemberCoverage[] | undefined,
    dependent: MemberDependent | undefined
  ) {
    let coverageStatus: CoverageStatus = CoverageStatus.Active;
    let hasActiveCoverage = false;
    let allCoveragesTerminated = false;
    if (coverages && dependent) {
      const fullName = dependent.fullName ?? '';
      //filter all current coverates that include the dependent
      const currentCoverages = coverages.filter(
        (coverage) =>
          coverage.sortNames?.includes(fullName) &&
          fullName !== '' &&
          coverage.current === true
      );
      if (currentCoverages.length !== 0) {
        hasActiveCoverage = currentCoverages.some(
          (coverage) =>
            coverage.terminationDate === null ||
            coverage.terminationDate === undefined ||
            coverage.terminationDate === ''
        );
        // check if all coverages are terminated
        allCoveragesTerminated = currentCoverages.every(
          (coverage) =>
            coverage.terminationDate !== null &&
            coverage.terminationDate !== undefined &&
            coverage.terminationDate !== ''
        );
      }
      coverageStatus = hasActiveCoverage
        ? CoverageStatus.Active
        : allCoveragesTerminated
          ? CoverageStatus.PlannedTermination
          : CoverageStatus.InActive;
    }
    return coverageStatus;
  }

  formatEmployee(input: MemberDetailResponse | undefined): EmployeeData {
    const result = {
      firstName: input?.firstName ?? Constants.StringEmpty,
      lastName: input?.lastName ?? Constants.StringEmpty,
      fullName: this.employeeService.formatFullName(
        input?.firstName,
        input?.middleName,
        input?.lastName
      ),
      birthdate: this.formatDate(input?.dob) ?? Constants.StringEmpty,
      gender: input?.gender ?? Constants.StringEmpty,
      status: input?.contractStatus ?? Constants.StringEmpty,
      statusColor: this.getStatusColor(input?.contractStatus),
      occupation: input?.occupation ?? Constants.StringEmpty,
      ssn: this.showSsn(input?.maskedSsn),
      salary: input?.salaries?.[0]?.annualSalary
        ? this.formatCurrency(input.salaries?.[0].annualSalary)
        : Constants.StringEmpty,
      hireDate: input?.salaries?.[0]?.startDate
        ? this.formatDate(input?.salaries?.[0].startDate)
        : Constants.StringEmpty,
      smokerStatus: input?.smokerStatus
        ? MemberSmokerStatus[input.smokerStatus]
        : Constants.StringEmpty,
      billDivision: input?.billDivision ?? Constants.StringEmpty,
      billDivisionNumber: input?.jobSiteCode ?? Constants.StringEmpty
    };

    return result;
  }

  showSsn(input: string | undefined) {
    if (input && input !== Constants.StringEmpty) {
      return input;
    } else {
      return 'Unknown';
    }
  }

  formatCurrency(amount: number | undefined): string {
    const currencyPipe = new CurrencyPipe('en-US');
    return currencyPipe.transform(amount) + Constants.StringEmpty;
  }

  formatDependent(input: MemberDependent) {
    if (input.relationship === MemberDependentRelationship.Child) {
      const childData: DependentData = {
        firstName: input.firstName ?? Constants.StringEmpty,
        lastName: input.lastName ?? Constants.StringEmpty,
        fullName: this.employeeService.formatFullName(
          input.firstName,
          input.middleName,
          input.lastName
        ),
        birthdate: this.formatDate(input.dob) ?? Constants.StringEmpty,
        gender: input.gender ?? MemberDependentGender.Unknown,
        status: input.status ?? MemberDependentStatus.Unknown,
        coverageStatus: CoverageStatus.Unknown,
        statusColor: this.getStatusColor(input.status),
        ssn: this.showSsn(input.maskedSsn),
        relationship: input.relationship,
        student: input.isStudent ? 'Yes' : 'No',
        disabled: input.isDisabled ? 'Yes' : 'No',
        smokerStatusString: Constants.StringEmpty
      };
      this.dependentsList?.push(childData);
    } else {
      const spouseData: DependentData = {
        firstName: input.firstName!,
        lastName: input.lastName!,
        fullName: this.employeeService.formatFullName(
          input.firstName,
          input.middleName,
          input.lastName
        ),
        birthdate: this.formatDate(input.dob),
        gender: input.gender ?? MemberDependentGender.Unknown,
        status: input.status ?? MemberDependentStatus.Unknown,
        coverageStatus: CoverageStatus.Unknown,
        statusColor: this.getStatusColor(input.status),
        ssn: this.showSsn(input.maskedSsn),
        relationship: input.relationship ?? MemberDependentRelationship.Unknown,
        smokerStatus: input.isSmoker !== undefined ? true : false,
        smokerStatusString: input.isSmoker ? 'Smoker' : 'Non-smoker'
      };

      this.dependentsList?.push(spouseData);
    }
  }

  formatDate(date: string | undefined): string {
    if (!date) {
      return Constants.StringEmpty;
    }
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString();
  }

  generalTableHeaders: DataTableHeaderOption[] = [
    { text: this.Description, key: 'description', width: '20%' },
    { text: this.Class, key: 'class', align: 'center', width: '10%' },
    { text: this.Benefit, key: 'benefit', align: 'right', width: '15%' },
    { text: this.Premium, key: 'premium', align: 'right', width: '15%' },
    {
      text: Constants.StringEmpty,
      key: 'spacer',
      align: 'center',
      width: '5%'
    },
    { text: this.Effective, key: 'effectiveDate', align: 'left', width: '15%' },
    { text: this.Termination, key: 'terminationDate', width: '20%' }
  ];

  absenceTableHeaders: DataTableHeaderOption[] = [
    { text: this.Description, key: 'description', width: '20%' },
    { text: this.Class, key: 'class', align: 'center', width: '10%' },
    { text: Constants.StringEmpty, key: 'benefit', width: '15%' },
    { text: this.Premium, key: 'premium', align: 'right', width: '15%' },
    {
      text: Constants.StringEmpty,
      key: 'spacer',
      align: 'center',
      width: '5%'
    },
    { text: this.Effective, key: 'effectiveDate', align: 'left', width: '15%' },
    { text: this.Termination, key: 'terminationDate', width: '20%' }
  ];

  getStatusColor(status?: string, terminationDate?: string) {
    if (terminationDate) {
      const termDate = new Date(terminationDate);
      const today = new Date();
      if (termDate > today) {
        status = CoverageStatus.PlannedTermination;
      }
    }
    switch (status) {
      case CoverageStatus.PlannedTermination:
        return '#FDD835';
      case CoverageStatus.Leave:
        return '#FDD835';
      case CoverageStatus.Active:
        return '#8ABF2E';
      case CoverageStatus.Terminated:
        return '#D32F2F';
      case CoverageStatus.InActive:
        return '#D32F2F';
      default:
        return CoverageStatus.Unknown;
    }
  }

  getDepCardTitle(input: number) {
    const resultNum = input + 1;
    return `Dependent ${resultNum}`;
  }

  modalClose() {
    this.showModal = false;
  }

  openDependentsModal(dependents: string[]) {
    this.showModal = true;
    this.modalList = dependents;
  }
}
